/**
 * The agency a signed-in person is working in, when they belong to more than one (TZ §17.2).
 *
 * The server is the authority: every request names its agency with `X-Tenant-Id`, and a name
 * the caller does not belong to is refused there. This module only remembers which agency this
 * browser last chose, so a reload comes back to the same one. Losing that memory is harmless:
 * the person is asked again.
 */

const STORAGE_KEY = "kapitan.activeAgency";

/** One agency the person can work in, as `/api/auth/tenants/` lists it. */
export interface AgencyChoice {
  id: number;
  name: string;
  role: string;
}

/**
 * What to do with the remembered agency, given the agencies the person belongs to.
 *
 * - none at all (a platform admin with no agency): `none`, nothing to header
 * - the remembered one is still a membership: `use`
 * - exactly one membership: `use` it, since there is nothing to choose
 * - several and nothing (or something stale) remembered: `choose`, a person must pick
 */
export type AgencyDecision =
  | { kind: "none" }
  | { kind: "use"; id: number }
  | { kind: "choose"; agencies: readonly AgencyChoice[] };

export function decideAgency(
  agencies: readonly AgencyChoice[],
  remembered: number | null,
): AgencyDecision {
  if (agencies.length === 0) {
    return { kind: "none" };
  }
  if (remembered !== null && agencies.some((agency) => agency.id === remembered)) {
    return { kind: "use", id: remembered };
  }
  const only = agencies[0];
  if (agencies.length === 1 && only !== undefined) {
    return { kind: "use", id: only.id };
  }
  return { kind: "choose", agencies };
}

/** Reads the remembered agency. Storage can be blocked (private windows); then there is none. */
export function readActiveAgency(): number | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const id = Number(raw);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export function writeActiveAgency(id: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(id));
  } catch {
    // Blocked storage: the person picks again next visit. Nothing else depends on the memory.
  }
}

export function clearActiveAgency(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored, nothing to clear.
  }
}
